import { GeneralInstructionIntent } from '@/intent';
import { RequestData } from '@ant-design/pro-table';
import { request } from '@/utils/request';
import { InstructionIntent } from './wallet';

export enum InstructionScheduleType {
  FUTURE = 'FUTURE',
  RECURRING = 'RECURRING'
}

export enum InstructionScheduleInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY'
}

export interface QuotePaymentDto {
  type?: string;
  attributes?: InstructionAttributeDto;
}

export interface InstructionAttributeDto {
  saveTransferPeer?: boolean;
  label?: string;
  transferPeerId?: string;
  receiverFirstName?: string;
  receiverSurname?: string;
  receiverFirstNameCN?: string;
  receiverSurnameCN?: string;
  recipient?: string;
  accountNumber?: string;
  paymentType?: string;
  paymentInfo?: string;
  bankAccount?: string;
  bankAccountProof?: string;
  bankAccountName?: string;
  billAccountNumber?: string;
  idPhoto?: string;
  name?: string;
  idNumber?: string;
  bankName?: string;
  bankAccountNumber?: string;
  receiverAccountNumber?: string;
  receiverAddress?: string;
  receiverIdType?: string;
  receiverIdNumber?: string;
  walletId?: string;
  bankCountry?: string;
  bankAddress?: string;
  bankSwiftCode?: string;
  beneficiaryName?: string;
  beneficiaryAddress?: string;
  beneficiaryAccountNumber?: string;
  bankCode?: string;
  merchant?: string;
  phoneNumber?: string;
  relationship?: string;
  address?: string;  // ✅ Generic address field
  country?: string;  // ✅ Generic country field
  beneficiaryCountry?: string;  // ✅ Alternative country field name
}

export interface PaymentInstructionsListItem {
  id: string;
  instructionId: string;
  feature?: string | null;
  type: string;
  user: {
    email: string;
    id: string;
    accountId?: string;
    name?: string;
    account_type?: string;
    company_name?: string;
    relationshipManager: {
      id: string;
      name: string;
    };
  };
  assetSymbol: string;
  amount: number;
  date: string;
  status: string;
  subStatus?: string | null;
  refId: string;
  method?: string;
}

export interface IntentDetailParserItem {
  type: string;
  refId: string;
  /** Feature source table (e.g., 'bill_payment' for legacy, 'general_instruction' for new) */
  feature?: string;
  id?: string;
}

export interface LocationItem {
  id?: string;
  name?: string;
  phone: string;
  address: string;
  latitude: number;
  timezone: string;
  longitude: number;
  responsiblePerson: string;
}

export interface BeneficiaryAttachment {
  id?: string;
  documentType?: string;
  status?: string;
  files?: Array<{ id?: string; name?: string; fileName?: string; documentKey?: string; documentUrl?: string; url?: string }>;
  details?: Record<string, { id: string; name?: string; fileName?: string; documentKey?: string; documentUrl?: string }>;
}

export enum Relationship {
  spouse = 'SPOUSE',
  son = 'SON',
  daughter = 'DAUGHTER',
  parent = 'PARENT',
  sibling = 'SIBLING',
  grandson = 'GRANDSON',
  grandparent = 'GRANDPARENT',
  granddaughter = 'GRANDDAUGHTER',
  unbornChildren = 'MY_UNBORN_CHILDREN',
  unbornGrandChildren = 'MY_UNBORN_GRANDCHILDREN',
  relative = 'RELATIVE',
  friend = 'FRIEND',
  partner = 'PARTNER',
  charity = 'CHARITY',
  director = 'DIRECTOR',
  shareholderPersonal = 'SHAREHOLDER_PERSONAL',
  shareholderCorporate = 'SHAREHOLDER_CORPORATE',
  corporation = 'CORPORATION',
  myself = 'MYSELF',
  others = 'OTHER'
}

export interface BeneficiaryFile {
  fileList: Array<{ uid?: string; name?: string; url?: string; status?: string; response?: { url?: string } }>;
}

export interface Beneficiary {
  id: string;
  relationship: Relationship;
  distribution?: number | null;
  firstName?: string;
  lastName?: string;
  localFirstName?: string;
  localSurname?: string;
  legalName?: string;
  additionalInfo?: string;
  attachments?: Array<BeneficiaryAttachment>;
  companyRegistrationNumber?: string;
  country?: string;
  dateOfBirth?: string | null;
  idPassportNumber?: string;
  idType?: 'Passport' | 'ID Card';
  mobile?: string;
  remarks?: string;
  reviewStatus: string;
  missingInfoRequirements?: string[];
  status: string;
  mobile_code?: string;
  mobile_number?: string;
  idFront?: BeneficiaryFile;
  idBack?: BeneficiaryFile;
  idPassport?: BeneficiaryFile;
  companyBr?: BeneficiaryFile;
  companyNar?: BeneficiaryFile;
  companyAtts?: BeneficiaryFile;
  organization?: string;
}

export interface InstructionRequestItem {
  accountId?: string;
  amount?: number;
  appointmentDateTimeFrom?: string;
  appointmentDateTimeTo?: string;
  createdAt: string;
  currency?: string;
  feeSnapshotId?: string;
  instructionRequestId?: string;
  location?: LocationItem;
  messageToRecipient?: string;
  remarks?: string;
  transferPeerId?: string;
  beneficiaries?: Array<Beneficiary>;
}

export interface GetInstructionsSearchParams {
  text?: string | string[];
  id?: string | string[];
}

export interface InstructionsSearchResponse {
  intent: string;
  refId: string;
  intentId: string;
  feature: string;
}

export enum InstructionMethod {
  BANK_TRANSFER = 'BANK_TRANSFER',
  BANK_PAYOUT = 'BANK_PAYOUT',
  BANK_INJECTION = 'BANK_INJECTION',
  CASH_DROP_OFF = 'CASH_DROP_OFF',
  CASH_PICKUP = 'CASH_PICKUP',
  UNIONPAY = 'UNIONPAY',
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_SWIFT = 'BANK_SWIFT',
  ALIPAY = 'ALIPAY'
}

export function intentParser(refId: string) {
  const intent = refId ? refId.substring(0, 2) : null;
  switch (intent) {
    case 'GI':
      return 'GENERAL_INSTRUCTION';
    case 'FI':
      return 'FEE';
    case 'CT':
      return 'CREDIT_TRANSACTION';
    default:
      return null;
  }
}

export function intentDetailUrlParser(record: IntentDetailParserItem) {
  let intent = intentParser(record.refId) ?? record.type;
  if (
    [
      GeneralInstructionIntent.PRECIOUS_METAL_BUY,
      GeneralInstructionIntent.PRECIOUS_METAL_SELL,
      GeneralInstructionIntent.PRECIOUS_METAL_WITHDRAWAL,
      GeneralInstructionIntent.IFAST_FUND_SUBSCRIPTION,
      GeneralInstructionIntent.IFAST_FUND_REDEMPTION,
      GeneralInstructionIntent.IFAST_FUND_OPEN_ACCOUNT,
      GeneralInstructionIntent.IFAST_FUND_DIVIDEND_PAYOUT
    ].includes(record.type as GeneralInstructionIntent)
  ) {
    intent = record.type;
  }

  // Handle legacy bill payment: feature='bill_payment' means data is from bill_payments table
  if (intent === InstructionIntent.BILL_PAYMENT && record.feature === 'bill_payment') {
    return '/instructions/legacy-bill-payments';
  }

  switch (intent) {
    case InstructionIntent.DEPOSIT_DIGITAL:
    case 'DIGITAL_DEPOSIT':
      return '/digital-custody/deposits';
    case 'FIAT_WITHDRAW':
    case InstructionIntent.WITHDRAW_FIAT:
      return '/instructions/fiat-withdraws';
    case InstructionIntent.WITHDRAW_DIGITAL:
    case 'DIGITAL_WITHDRAW':
      return '/digital-custody/withdraws';
    case InstructionIntent.DEPOSIT_FIAT:
    case 'FIAT_DEPOSIT':
      return '/instructions/fiat-deposits';
    case InstructionIntent.COMMODITIES:
    case InstructionIntent.COMMODITIES_DEPOSIT:
    case InstructionIntent.COMMODITIES_WITHDRAW:
    case InstructionIntent.TRADER_MINT:
    case InstructionIntent.TRADER_BURN:
    case InstructionIntent.REPAY:
    case 'BORROW':
    case 'CREDIT_TRANSACTION':
      return '/cms/transactions';
    case InstructionIntent.EXCHANGE:
      return '/instructions/exchanges';
    case InstructionIntent.BILL_PAYMENT:
      return '/instructions/general-instruction';
    case InstructionIntent.TRANSFER_SELF:
    case InstructionIntent.TRANSFER_P2P:
    case 'WALLET_TRANSFER':
      return '/instructions/transfers';
    case 'GENERAL_INSTRUCTION':
    case InstructionIntent.FOREX:
    case InstructionIntent.SECURITIES_TRANSFER:
    case InstructionIntent.INSURANCE:
    case InstructionIntent.ASSET_INJECTION:
    case InstructionIntent.ADJUSTMENT:
    case InstructionIntent.CARD_PAYMENT:
    case InstructionIntent.COMMODITIES:
    case InstructionIntent.CHARGE:
    case InstructionIntent.TRANSFER:
    case InstructionIntent.FUND_SUBSCRIPTION:
    case InstructionIntent.BOND_SUBSCRIPTION:
    case InstructionIntent.SECURITIES_APPLICATION:
    case InstructionIntent.EXCHANGE_SUB_ACCOUNT_APPLICATION:
    case InstructionIntent.EXCHANGE_SUB_ACCOUNT_TRANSFER:
    case InstructionIntent.EXCHANGE_SUB_ACCOUNT_MONTHLY_FEE:
    case InstructionIntent.CHANGE_BENEFICIARIES:
    case InstructionIntent.INSURANCE_ENQUIRY:
    case InstructionIntent.SETTLEMENT:
    case InstructionIntent.FUND_DISTRIBUTION:
    case InstructionIntent.COMMISSION:
    case InstructionIntent.ASSET_WITHDRAWAL:
      return '/instructions/general-instruction';
    case InstructionIntent.FEE_PAYOUT:
    case 'FEE':
      return '/instructions/fee-payouts';
    case InstructionIntent.EARN_REDEMPTION:
      return '/earn/redemptions';
    case InstructionIntent.EARN_SUBSCRIPTION:
      return '/earn/subscriptions';
    case GeneralInstructionIntent.PRECIOUS_METAL_BUY:
    case GeneralInstructionIntent.PRECIOUS_METAL_SELL:
    case GeneralInstructionIntent.PRECIOUS_METAL_WITHDRAWAL:
      return '/precious-metal/instructions';
    case GeneralInstructionIntent.IFAST_FUND_SUBSCRIPTION:
    case GeneralInstructionIntent.IFAST_FUND_REDEMPTION:
    case GeneralInstructionIntent.IFAST_FUND_OPEN_ACCOUNT:
    case GeneralInstructionIntent.IFAST_FUND_DIVIDEND_PAYOUT:
      return '/fund/instructions';
    default:
      return null;
  }
}

const GENERAL_INSTRUCTION_DETAIL_PATH = '/instructions/general-instruction';

const pendingInstructionTypeUrlMap: Record<string, string> = {
  'digital-deposit': '/digital-custody/deposits',
  'digital-withdraw': '/digital-custody/withdraws',
  'fiat-deposit': '/instructions/fiat-deposits',
  'fiat-withdraw': '/instructions/fiat-withdraws',
  'bill-payment': '/instructions/legacy-bill-payments',
  'wallet-transfer': '/instructions/transfers'
};

type PendingInstructionDetailRecord = Pick<
  PaymentInstructionsListItem,
  'id' | 'instructionId' | 'feature' | 'type' | 'refId'
>;

/**
 * Resolves the detail route and the identifier expected by that route.
 * General instruction pages require the source row ID; other detail pages use
 * the parent instruction ID exposed by the pending-instructions API.
 */
export function getPendingInstructionDetailPath(record: PendingInstructionDetailRecord): string | null {
  const detailPath =
    pendingInstructionTypeUrlMap[record.type] ??
    intentDetailUrlParser({
      type: record.type,
      refId: record.refId,
      feature: record.feature ?? undefined
    });

  if (!detailPath) return null;

  const detailId = detailPath === GENERAL_INSTRUCTION_DETAIL_PATH ? record.id : record.instructionId;
  return `${detailPath}/${detailId}`;
}

export async function queryTotalPendingPaymentInstructions(params: any): Promise<RequestData<PaymentInstructionsListItem>> {
  return await request<RequestData<PaymentInstructionsListItem>>('/instructions/pending-count', {
    params: params
  });
}

export async function queryPendingPaymentInstructions(params: any): Promise<RequestData<PaymentInstructionsListItem>> {
  return await request<RequestData<PaymentInstructionsListItem>>('/instructions/pending', {
    params: params
  });
}

/**
 * Statistics for pending payment instructions dashboard
 */
export type PendingPaymentStatistics = {
  /** Count of instructions awaiting initial review, excluding funding and approval sub-statuses */
  newCount: number;
  /** Count of instructions with status PROCESSING */
  processingCount: number;
  /** Count of instructions with status PENDING or PENDING_PAYMENT */
  pendingCount: number;
  /** Count of instructions with sub_status PENDING_FUNDING */
  pendingFundingCount: number;
  /** Count of instructions with sub_status PENDING_APPROVE */
  pendingApprovalCount: number;
  /** Breakdown of counts by intent type */
  intentBreakdown: Array<{ intent: string; count: number }>;
  /** Total unsettled amounts grouped by currency */
  totalUnsettledAmounts: Record<string, string>;
};

/**
 * Fetches aggregated statistics for pending payment instructions dashboard
 * @returns Statistics including status counts, intent breakdown, and amounts by currency
 */
export async function getPendingPaymentStatistics(): Promise<PendingPaymentStatistics> {
  return request<PendingPaymentStatistics>('/instructions/pending-statistics');
}

export function getInstructionsSearch(params: GetInstructionsSearchParams) {
  return request<InstructionsSearchResponse[]>('/instructions/search', {
    params: params,
    getResponse: true
  });
}
